import { useEffect, useState } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { LoadFailure } from '../../components/LoadFailure';
import { useAuth } from '../../core/authContext';
import { feedbackSurveyUrl } from '../../core/surveyLink';
import {
  CardHeader,
  CardHeading,
  CardSubline,
  ConfirmedLine,
  GroupLabel,
  Loading,
  Mark,
  NotConfirmedPill,
  RowsFrame,
  SaveFailed,
  SettingsCard,
} from './cardParts';
import { confirmedLine } from './confirmation';
import { bandsFromDraft, draftDirty, draftFromBands, type DraftBand } from './criteriaEdit';
import { criteriaDifferenceCount, criteriaGroups, criteriaGroupsAgainstRecord, orderBands, schoolYearLabel, versionLine, type CriteriaCondition, type CriteriaGroup } from './criteriaView';
import { ExitCriteriaEditor } from './ExitCriteriaEditor';
import { CriteriaRecordChangedError, type Confirmation, type ExitCriteriaSettings, type GradeBand, type ReviewedConfirmation, type SetupState, type StateRecordVersion } from './settingsApi';
import { SETTINGS } from './tokens';

export const EXIT_CRITERIA_CONFIRMED_TOAST = 'Exit criteria confirmed. Plans and reports use them now.';
export const EXIT_CRITERIA_SAVED_TOAST = 'Exit criteria saved. Plans and reports use them now.';
export const CRITERIA_DISCARD_BODY = 'Your changes to the exit criteria won’t be saved. Your district’s current criteria stay in place.';

type Props = {
  source: () => Promise<ExitCriteriaSettings>;
  confirm: (reviewed: ReviewedConfirmation) => Promise<SetupState>;
  save: (gradeBands: GradeBand[], reviewed: ReviewedConfirmation) => Promise<ExitCriteriaSettings>;
  onToast: (message: string) => void;
  onDirtyChange: (dirty: boolean) => void;
  onSavingChange: (saving: boolean) => void;
  requestLeave: (proceed: () => void) => void;
};

function recordVersion(settings: ExitCriteriaSettings): StateRecordVersion | null {
  const versions = settings.stateRecord?.versions ?? [];
  if (versions.length === 0 || !settings.effective) return null;
  const pinned = settings.followsStateRecord ? undefined : settings.districtCriteria?.basedOn.stateRecordVersion;
  const basedOn = pinned ? versions.find((v) => v.effectiveFrom === pinned) : undefined;
  const inForce = versions.find((v) => v.effectiveFrom === settings.effective?.effectiveFrom);
  return basedOn ?? inForce ?? null;
}

function bandsOf(settings: ExitCriteriaSettings, version: StateRecordVersion | null): GradeBand[] {
  return settings.effective?.gradeBands ?? version?.gradeBands ?? [];
}

function sameRecordRevision(current: ExitCriteriaSettings, schoolYear: string, reviewed: StateRecordVersion): boolean {
  const inForce = recordVersionInForce(current);
  return current.schoolYear === schoolYear && inForce !== null && inForce.effectiveFrom === reviewed.effectiveFrom && inForce.verifiedOn === reviewed.verifiedOn;
}

function conflictMessage(conflict: 'record' | 'district' | null, stateName: string): string | null {
  if (conflict === 'district') return 'Your district’s criteria were changed by another admin while you were editing. Reload the page to start from the current criteria.';
  if (conflict === 'record') return `${stateName}’s record changed while you were editing. Reload the page to start from the current record.`;
  return null;
}

function recordVersionInForce(settings: ExitCriteriaSettings): StateRecordVersion | null {
  const versions = settings.stateRecord?.versions ?? [];
  const inForce = versions.filter((v) => v.effectiveFrom <= settings.schoolYear);
  return inForce[inForce.length - 1] ?? null;
}

function SourceLink({ href, label }: { href: string; label: string }) {
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ color: SETTINGS.primary, fontWeight: 400 }}>
      {label}
    </Link>
  );
}

function ConditionLine({ first, label, condition }: { first: boolean; label: string; condition: CriteriaCondition }) {
  const struck = condition.removed ? 's' : 'span';
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '16px', color: SETTINGS.ink, fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px' }}>
      <Typography
        component="span"
        sx={{ width: SETTINGS.labelWidth, flexShrink: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', fontWeight: first ? 700 : 400, color: first ? SETTINGS.ink : SETTINGS.muted }}
      >
        {first ? label : 'and'}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px', fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}>
        <Typography component={struck} sx={{ fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', color: condition.removed ? SETTINGS.muted : 'inherit' }}>
          {condition.measure}
        </Typography>
        {condition.mark && <Mark>{condition.mark}</Mark>}
      </Box>
      <Typography component={struck} sx={{ fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', fontWeight: condition.removed ? 400 : 700, whiteSpace: 'nowrap', color: condition.removed ? SETTINGS.muted : 'inherit' }}>
        {`≥ ${condition.value}`}
      </Typography>
    </Box>
  );
}

function Group({ group }: { group: CriteriaGroup }) {
  return (
    <Box data-testid="criteria-group" data-label={group.label} sx={{ '& + &': { borderTop: `1px solid ${SETTINGS.divider}` } }}>
      <GroupLabel>{group.label}</GroupLabel>
      {group.noExit && (
        <Typography sx={{ px: '16px', pb: '12px', fontFamily: SETTINGS.font, fontSize: 14, lineHeight: '20px', color: SETTINGS.muted }}>
          Students can’t exit on this assessment.
        </Typography>
      )}
      {group.rows.map((row) => (
        <Box
          key={row.outcome}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            p: '12px 16px',
            ...(row.review ? { mx: '12px', px: '12px', borderRadius: '8px', bgcolor: SETTINGS.tint } : {}),
          }}
        >
          {row.conditions.map((c, i) => (
            <ConditionLine key={`${row.outcome}-${c.measure}`} first={i === 0} label={row.label} condition={c} />
          ))}
          {row.note && (
            <Typography sx={{ ml: `${SETTINGS.labelWidth + 16}px`, fontFamily: SETTINGS.font, fontSize: 14, lineHeight: '20px', color: SETTINGS.muted }}>
              {row.note}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

function NoRecord({ stateName, schoolYear }: { stateName: string; schoolYear?: string }) {
  const { user } = useAuth();
  const title = schoolYear
    ? `We don’t have ${stateName}’s exit criteria for ${schoolYearLabel(schoolYear)} yet`
    : `We don’t have ${stateName}’s exit criteria yet`;
  return (
    <SettingsCard labelledBy="exit-criteria-title">
      <CardHeader>
        <CardHeading id="exit-criteria-title">{title}</CardHeading>
        <CardSubline>Send us your district’s exit criteria and we’ll add them.</CardSubline>
      </CardHeader>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: '16px', borderTop: `1px solid ${SETTINGS.divider}` }}>
        <Button component="a" href={feedbackSurveyUrl(user?.id)} target="_blank" rel="noopener noreferrer" variant="outlined">
          Send feedback
        </Button>
      </Box>
    </SettingsCard>
  );
}

function CheckingLine({ count, stateName }: { count: number; stateName: string }) {
  const noun = count === 1 ? 'difference' : 'differences';
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', mt: '8px', maxWidth: 720, color: SETTINGS.mark }}>
      <AccessTimeIcon sx={{ fontSize: 20, mt: '2px' }} />
      <Typography sx={{ fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px' }}>
        {`${count} ${noun} from our ${stateName} record. Already in use for your district. We’re checking whether ${stateName}’s rule has changed.`}
      </Typography>
    </Box>
  );
}

export function ExitCriteriaTab({ source, confirm, save, onToast, onDirtyChange, onSavingChange, requestLeave }: Props) {
  const [settings, setSettings] = useState<ExitCriteriaSettings | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [conflict, setConflict] = useState<'record' | 'district' | null>(null);
  const [criteriaChanged, setCriteriaChanged] = useState(false);
  const [draft, setDraft] = useState<DraftBand[] | null>(null);

  useEffect(() => {
    let active = true;
    source()
      .then((next) => {
        if (!active) return;
        setSettings(next);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [source]);

  const version = settings ? recordVersion(settings) : null;
  const reviewed = settings ? recordVersionInForce(settings) : null;
  const bands = settings ? bandsOf(settings, version) : [];
  const dirty = draft !== null && draftDirty(draft, bands);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  useEffect(() => {
    onSavingChange(busy);
  }, [busy, onSavingChange]);

  useEffect(() => () => onSavingChange(false), [onSavingChange]);

  if (failed) return <LoadFailure tokens={SETTINGS} />;
  if (settings === null) return <Loading />;

  const stateName = settings.stateName || settings.state;
  if (!settings.stateRecord) return <NoRecord stateName={stateName} />;
  if (version === null || reviewed === null) return <NoRecord stateName={stateName} schoolYear={settings.schoolYear} />;

  const confirmation: Confirmation | undefined = settings.exitCriteriaConfirmed;
  const guidance = versionLine(version);

  const onConfirm = () => {
    if (busy) return;
    setBusy(true);
    setSaveFailed(false);
    setCriteriaChanged(false);
    confirm({ schoolYear: settings.schoolYear, stateRecordVersion: reviewed.effectiveFrom, verifiedOn: reviewed.verifiedOn, ...(settings.changedAt ? { changedAt: settings.changedAt } : {}) })
      .then((state) => {
        if (!state.exitCriteriaConfirmed) {
          return source().then((next) => {
            setSettings(next);
            setCriteriaChanged(true);
          });
        }
        setSettings((current) => (current ? { ...current, exitCriteriaConfirmed: state.exitCriteriaConfirmed } : current));
        onToast(EXIT_CRITERIA_CONFIRMED_TOAST);
        return undefined;
      })
      .catch((e: unknown) => {
        if (e instanceof CriteriaRecordChangedError) {
          setSettings(e.current);
          setCriteriaChanged(true);
        } else {
          setSaveFailed(true);
        }
      })
      .finally(() => setBusy(false));
  };

  const onSave = () => {
    if (busy || draft === null) return;
    setBusy(true);
    setSaveFailed(false);
    setConflict(null);
    save(bandsFromDraft(draft), { schoolYear: settings.schoolYear, stateRecordVersion: reviewed.effectiveFrom, verifiedOn: reviewed.verifiedOn, ...(settings.changedAt ? { changedAt: settings.changedAt } : {}) })
      .then((saved) => {
        setSettings(saved);
        setDraft(null);
        onToast(EXIT_CRITERIA_SAVED_TOAST);
      })
      .catch((e: unknown) => {
        if (e instanceof CriteriaRecordChangedError) setConflict(sameRecordRevision(e.current, settings.schoolYear, reviewed) ? 'district' : 'record');
        else setSaveFailed(true);
      })
      .finally(() => setBusy(false));
  };

  if (draft !== null) {
    return (
      <ExitCriteriaEditor
        stateName={stateName}
        draft={draft}
        record={reviewed.gradeBands}
        busy={busy}
        saveFailed={saveFailed}
        failure={conflictMessage(conflict, stateName)}
        onChange={setDraft}
        onCancel={() => requestLeave(() => setDraft(null))}
        onReset={() => setDraft(draftFromBands(orderBands(reviewed.gradeBands)))}
        onSave={onSave}
      />
    );
  }

  const edited = !settings.followsStateRecord;
  const subline = edited
    ? `Edited from the ${stateName} state default · ${guidance} · `
    : confirmation
      ? `${stateName} state default, unchanged · ${guidance} · `
      : `${stateName} state default · ${guidance} · `;
  const sourceLabel = edited ? `View ${stateName} source` : 'View source';
  const title = edited || confirmation ? 'Your district’s exit criteria' : `${stateName}’s exit criteria`;
  const groups = edited ? criteriaGroupsAgainstRecord(bands, version.gradeBands, stateName) : criteriaGroups(bands);
  const differences = edited ? criteriaDifferenceCount(bands, version.gradeBands) : 0;
  const checking = edited && differences > 0 && settings.differences?.status === 'open';
  const startEditing = () => {
    setSaveFailed(false);
    setConflict(null);
    setCriteriaChanged(false);
    setDraft(draftFromBands(orderBands(bands)));
  };
  const editButton = (
    <Button variant="text" onClick={startEditing} startIcon={<EditOutlinedIcon />}>
      Edit criteria
    </Button>
  );

  return (
    <SettingsCard labelledBy="exit-criteria-title">
      <CardHeader aside={confirmation ? editButton : <NotConfirmedPill />}>
        <CardHeading id="exit-criteria-title">{title}</CardHeading>
        <CardSubline>
          {subline}
          <SourceLink href={version.sourceUrl} label={sourceLabel} />
        </CardSubline>
        {confirmation && <ConfirmedLine>{confirmedLine(confirmation)}</ConfirmedLine>}
        {checking && <CheckingLine count={differences} stateName={stateName} />}
      </CardHeader>
      <RowsFrame>
        {groups.map((group) => (
          <Group key={group.key} group={group} />
        ))}
      </RowsFrame>
      {!confirmation && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' }}>
          <SaveFailed visible={saveFailed} />
          {criteriaChanged && (
            <Typography role="alert" sx={{ fontFamily: SETTINGS.font, fontSize: 14, lineHeight: '20px', color: SETTINGS.error }}>
              {`${stateName}’s criteria changed while you were looking. The current criteria are shown; review them again before confirming.`}
            </Typography>
          )}
          <Button variant="outlined" onClick={startEditing} disabled={busy}>
            Edit criteria
          </Button>
          <Button variant="contained" onClick={onConfirm} disabled={busy}>
            This matches our district
          </Button>
        </Box>
      )}
    </SettingsCard>
  );
}
